import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../App";
import { Download, ChevronDown, ChevronRight } from "lucide-react";

/**
 * Audit log — read-only, paginated, filterable, CSV export.
 * Expandable `details` JSON per row.
 */

export default function AdminAuditLog() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ admin_email: "", action: "", target_id: "" });
  const [openRow, setOpenRow] = useState(null);

  const fetchList = useCallback(() => {
    setData(null);
    const params = { page, limit: 100 };
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    apiClient
      .get("/admin/audit-log", { params })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || "Failed to load audit log"));
  }, [page, filters]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const exportCSV = () => {
    if (!data || !Array.isArray(data.entries)) return;
    const rows = [
      ["timestamp", "admin_email", "action", "target_type", "target_id", "request_id", "details"],
      ...data.entries.map((e) => [
        e.timestamp,
        e.admin_email,
        e.action,
        e.target_type,
        e.target_id,
        e.request_id,
        JSON.stringify(e.details || {}).replaceAll('"', '""'),
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c ?? "").replaceAll('"', '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `admin-audit-log-page${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="admin-audit-log">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Audit log</h1>
          <p className="text-sm text-slate-500 mt-1">
            Every admin mutation, newest first.
          </p>
        </div>
        <button
          onClick={exportCSV}
          disabled={data === null}
          className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white disabled:opacity-40"
          data-testid="admin-audit-export"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </header>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap gap-3">
        <input
          placeholder="Filter by admin email"
          value={filters.admin_email}
          onChange={(e) => setFilters((f) => ({ ...f, admin_email: e.target.value }))}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex-1 min-w-[200px]"
          data-testid="admin-audit-filter-email"
        />
        <input
          placeholder="Filter by action (e.g. user.suspended)"
          value={filters.action}
          onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex-1 min-w-[200px]"
          data-testid="admin-audit-filter-action"
        />
        <input
          placeholder="Filter by target_id"
          value={filters.target_id}
          onChange={(e) => setFilters((f) => ({ ...f, target_id: e.target.value }))}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex-1 min-w-[200px]"
          data-testid="admin-audit-filter-target"
        />
        <button
          onClick={() => {
            setPage(1);
            fetchList();
          }}
          className="px-3 py-2 bg-slate-900 text-white rounded-lg text-sm"
          data-testid="admin-audit-apply"
        >
          Apply
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm mb-4">
          {error}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {data === null ? (
          <div className="p-4" data-testid="admin-audit-loading">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-10 bg-slate-100 rounded animate-pulse my-2" />
            ))}
          </div>
        ) : data.entries.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No audit entries.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-slate-600">
                <tr>
                  <th className="w-8" />
                  <th className="px-3 py-3 font-medium">Timestamp</th>
                  <th className="px-3 py-3 font-medium">Admin</th>
                  <th className="px-3 py-3 font-medium">Action</th>
                  <th className="px-3 py-3 font-medium">Target</th>
                  <th className="px-3 py-3 font-medium">Request ID</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e, i) => (
                  <AuditRow
                    key={`${e.timestamp}-${i}`}
                    entry={e}
                    open={openRow === i}
                    onToggle={() => setOpenRow((cur) => (cur === i ? null : i))}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data !== null && (
        <div className="flex items-center justify-between mt-4 text-sm text-slate-600">
          <span>
            {data.total} entries · page {data.page}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40"
            >
              Previous
            </button>
            <button
              disabled={page * data.limit >= data.total}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditRow({ entry, open, onToggle }) {
  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={onToggle}>
        <td className="px-2 py-3 text-slate-400">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </td>
        <td className="px-3 py-3 font-mono text-xs text-slate-600 whitespace-nowrap">
          {fmtTS(entry.timestamp)}
        </td>
        <td className="px-3 py-3 truncate max-w-[240px]">{entry.admin_email || "—"}</td>
        <td className="px-3 py-3 font-medium">{entry.action}</td>
        <td className="px-3 py-3 text-xs">
          <div className="text-slate-500">{entry.target_type}</div>
          <div className="font-mono text-slate-800 truncate max-w-[220px]">{entry.target_id || "—"}</div>
        </td>
        <td className="px-3 py-3 font-mono text-xs text-slate-500">{entry.request_id}</td>
      </tr>
      {open && (
        <tr className="bg-slate-50 border-b border-slate-100">
          <td />
          <td colSpan={5} className="px-3 py-3">
            <pre className="text-xs bg-white border border-slate-200 rounded-lg p-3 overflow-x-auto">
              {JSON.stringify(entry.details || {}, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function fmtTS(v) {
  if (typeof v !== "string" || v.length < 10) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return v;
  }
}
